import React from 'react';
import { PLACE_RESULT_TYPE, URGENT_CARE_SUGGESTION } from 'utils/constants';
import type { ResultType } from 'types/common.types';
import UrgentCareAlternateSearchDescription from './UrgentCareAlternateSearchDescription';
import type { AlternateSearchSuggestion } from './searchSuggestion.types';

interface SearchInput {
  text: string;
  specialtyId?: number;
  subspecialtyId?: number;
  type: ResultType;
}

export function shouldSuggestUrgentCare(searchInput: SearchInput): boolean {
  if (searchInput.type !== PLACE_RESULT_TYPE) return false;

  // first check the subspecialty id if it exists - this is a more efficient way to check than using a regex on the text
  const { subspecialtyId } = searchInput;
  const inclusionSubspecialtyIds = [
    549, // Emergency Care Clinic
    449, // General Acute Care
    451, // Critical Access Hospital
  ];

  if (subspecialtyId && inclusionSubspecialtyIds.includes(subspecialtyId)) {
    return true;
  }

  // there are some subspecialties that include "emergency" in the name but should not suggest urgent care
  // early return false for those cases
  const exclusionSubspecialtyIds = [
    611, // Emergency Response System Companies
  ];
  if (subspecialtyId && exclusionSubspecialtyIds.includes(subspecialtyId)) {
    return false;
  }

  const { text } = searchInput;

  // skip "non-emergency" or "non emergency" variations
  if (/\bnon[-\s]?em(?:er|re|r)g(?:en|an|n)?c?(?:y|ie)\b/i.test(text)) return false;

  // matching on variations of "Emergency" (including common misspellings)
  if (/\bem(?:er|re|r)g(?:en|an|n)?c?(?:y|ie)\b/i.test(text)) return true;

  // matching on "E.R." or "E.D." (abbreviations for emergency room/department)
  if (/\be\.?\s*[-.]?\s*[rd]\.?\b/i.test(text)) return true;

  // matching on "Acute Care" or "Acute Hospital"
  if (/\bacute\s+(?:care|hospital)\b/i.test(text)) return true;

  // matching on "Critical Access" (hospital/care)
  if (/\bcritical\s+access\b/i.test(text)) return true;

  return false;
}

export default function getAlternateSearchSuggestion(
  searchInput: SearchInput
): AlternateSearchSuggestion | null {
  if (shouldSuggestUrgentCare(searchInput)) {
    return {
      title: 'Try Urgent Care',
      description: <UrgentCareAlternateSearchDescription />,
      suggestion: URGENT_CARE_SUGGESTION,
    };
  }

  return null;
}
